import { useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import {
  SettledDailyReportResponse,
  SettledReportDiscount,
  getSettlementDateReport,
  getSettlementReport,
} from "../../../../api/settlement";
import { pubState } from "../../../../recoil/pub.atom";
import {
  MainWrapper,
  PageContentHeader,
  RadioBox,
  ReportContentTable,
  ReportContentTableBodyItem,
  ReportContentTableHeaderItem,
  ReportFilterHeader,
  ReportFilterWrapper,
  ReportHeader,
  ReportMoreInfo,
  ReportWrapper,
} from "./style";
import ReportCalender from "../calender/report-calender";
import ReportGraph from "./component/report-graph";
import MoreInfo, {
  DialogPayType,
} from "../../../timer-controller/components/sit-in/components/more";

interface props {
  setIsReport: (value: boolean) => void;
  setDailyDate: (value: string) => void;
}

const ReportPage = ({ setIsReport, setDailyDate }: props) => {
  const pub = useRecoilValue(pubState);
  const [report, setReport] = useState<SettledDailyReportResponse[]>([]);
  const [income, setIncome] = useState<number>(0);
  const [incomeCash, setIncomeCash] = useState<number>(0);
  const [incomeCard, setIncomeCard] = useState<number>(0);
  const [incomeBank, setIncomeBank] = useState<number>(0);
  const [expenditure, setExpenditure] = useState<number>(0);
  const [payCount, setPayCount] = useState<number>(0);
  const [payResult, setPayResult] = useState<number>(0);
  const [seed, setSeed] = useState<number>(0);
  const [totalDiscount, setTotalDiscount] = useState<number>(0);
  const [entryPoint, setEntryPoint] = useState<number>(0);
  const [tournamentPoint, setTournamentPoint] = useState<number>(0);
  const [transactionPoint, setTransactionPoint] = useState<number>(0);
  const [royaltySeed, setRoyaltySeed] = useState<number>(0);
  const [incomeSeed, setIncomeSeed] = useState<number>(0);
  const [expenseSeed, setExpenseSeed] = useState<number>(0);
  const [royaltyCarrot, setRoyaltyCarrot] = useState<number>(0);
  const [expenseCarrot, setExpenseCarrot] = useState<number>(0);
  const [carrot, setCarrot] = useState<number>(0);
  const [isHover, setIsHover] = useState<string>("");
  const [dateFilterType, setDateFilterType] = useState<string>("this_week");
  const [firstDate, setFirstDate] = useState<string>("");
  const [lastDate, setLastDate] = useState<string>("");
  const [reportDate, setReportDate] = useState<string>();
  const [expenseType, setExpenseType] = useState<DialogPayType[]>([]);
  const [discountList, setDiscountList] = useState<[SettledReportDiscount[]]>([
    [],
  ]);
  const [calenderOpen, setCalenderOpen] = useState<boolean>(false);
  const [getPoint, setGetPoint] = useState<number>(0);
  const [depositPoint, setDepositPoint] = useState<number>(0);
  const [promotionPoint, setPromotionPoint] = useState<number>(0);

  const getData = async () => {
    if (!pub) return;
    try {
      const res =
        dateFilterType === "date" && firstDate !== "" && lastDate !== ""
          ? await getSettlementDateReport(
              pub.id,
              dateFilterType,
              firstDate,
              lastDate,
            )
          : await getSettlementReport(pub.id, dateFilterType);
      setReport(res.daily);
      setEntryPoint(res.entryPoint);
      setTournamentPoint(res.tournamentPoint);
      setTransactionPoint(res.transactionPoint);
      setDiscountList(res.discount);
      setRoyaltySeed(res.royaltySeed);
      setIncomeSeed(res.incomeSeed);
      setExpenseSeed(res.expenseSeed);
      setSeed(res.royaltySeed + res.incomeSeed - res.expenseSeed);
      setRoyaltyCarrot(res.royaltyCarrot ?? 0);
      setExpenseCarrot(res.expenseCarrot ?? 0);
      setCarrot((res.royaltyCarrot ?? 0) - (res.expenseCarrot ?? 0));
      setGetPoint(res.getPoint);
      setDepositPoint(res.depositPoint);
      setPromotionPoint(res.promotionPoint);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (!report) return;
    let cash = 0,
      card = 0,
      bank = 0,
      revenue = 0,
      expense = 0,
      count = 0,
      finalNetProfit = 0;
    const expenseList: DialogPayType[] = [
      { name: "할인", value: totalDiscount, unit: "" },
    ];

    report.forEach((item) => {
      cash += item.totalCashRevenue;
      card += item.totalCardRevenue;
      bank += item.totalBankRevenue;
      revenue += item.totalRevenue;
      count += item.entryCount;
      finalNetProfit += item.finalNetProfit;

      item.expense.forEach(({ description, amount }) => {
        expense += amount;
        expenseList.push({ name: description, value: amount, unit: "원" });
      });
    });

    setIncomeCash(cash);
    setIncomeCard(card);
    setIncomeBank(bank);
    setIncome(revenue);
    setPayCount(count);
    setPayResult(finalNetProfit);
    setExpenseType(expenseList);
    setExpenditure(expense + totalDiscount);
  }, [report, totalDiscount]);

  useEffect(() => {
    if (dateFilterType === "date") return;
    getData();
  }, [dateFilterType]);

  useEffect(() => {
    setReportDate(
      `${formatLabelsDate(firstDate, "", true)} - ${formatLabelsDate(
        lastDate,
        "",
        true,
      )}`,
    );
    return;
  }, [firstDate, lastDate]);

  useEffect(() => {
    let discount = 0;
    discountList.forEach((list) => {
      list.forEach((item) => {
        discount += item.amount;
      });
    });
    setTotalDiscount(discount);
  }, [discountList]);

  const PointPayType = useMemo(() => {
    return [
      {
        name: `매장 > 회원(프라이즈)`,
        value: tournamentPoint / 10000,
        unit: "E",
      },
      {
        name: `회원 사용 (소멸)`,
        value: entryPoint / 10000,
        unit: "E",
      },
      {
        name: `수동 지급 매장이용권`,
        value: transactionPoint / 10000,
        unit: "E",
      },
      {
        name: `수동 차감 매장이용권`,
        value: Math.abs(getPoint / 10000),
        unit: "E",
      },
      {
        name: `디파짓 매장이용권`,
        value: Math.abs(depositPoint / 10000),
        unit: "E",
      },
      {
        name: `프로모션 매장이용권`,
        value: Math.abs(promotionPoint / 10000),
        unit: "E",
      },
    ];
  }, [
    transactionPoint,
    entryPoint,
    tournamentPoint,
    getPoint,
    depositPoint,
    promotionPoint,
  ]);

  const SeedPayType = useMemo(
    () => [
      {
        name: `매출 래빗`,
        value: incomeSeed,
        unit: "RB",
      },
      {
        name: `지출 래빗`,
        value: expenseSeed,
        unit: "RB",
      },
      {
        name: `본사 지급 및 회수 래빗`,
        value: royaltySeed,
        unit: "RB",
      },
      {
        name: `정산 통합 래빗`,
        value: seed,
        unit: "RB",
        weight: true,
      },
    ],
    [incomeSeed, expenseSeed, royaltySeed, seed],
  );

  const CarrotPayType = useMemo(
    () => [
      {
        name: `지출 캐럿`,
        value: expenseCarrot,
        unit: "CR",
      },
      {
        name: `본사 지급 및 회수 캐럿`,
        value: royaltyCarrot,
        unit: "CR",
      },
      {
        name: `정산 통합 캐럿`,
        value: carrot,
        unit: "CR",
        weight: true,
      },
    ],
    [expenseCarrot, royaltyCarrot, carrot],
  );

  const IncomeType = useMemo(
    () => [
      {
        name: "현금",
        value: incomeCash,
        unit: "원",
      },
      {
        name: "카드",
        value: incomeCard,
        unit: "원",
      },
      {
        name: "계좌",
        value: incomeBank,
        unit: "원",
      },
    ],
    [incomeCash, incomeCard, incomeBank],
  );

  const Financial = useMemo(() => {
    const base = [
      {
        name: "결제 건수",
        value: payCount?.toLocaleString(),
        unit: "건",
      },
      {
        name: "매출액",
        value: income?.toLocaleString(),
        unit: "원",
        type: IncomeType,
      },
      {
        name: "지출액",
        value: expenditure?.toLocaleString(),
        unit: "원",
        type: expenseType,
      },
      {
        name: "정산액",
        value: payResult?.toLocaleString(),
        unit: "원",
      },
      {
        name: "매장 이용권",
        value: "더보기",
        unit: "E",
        type: PointPayType,
      },
      {
        name: "통합 래빗",
        value: seed.toLocaleString(),
        unit: "RABBIT",
        type: SeedPayType,
      },
      {
        name: "통합 캐럿",
        value: carrot.toLocaleString(),
        unit: "CARROT",
        type: CarrotPayType,
      },
    ];

    return base;
  }, [
    payCount,
    income,
    IncomeType,
    expenditure,
    expenseType,
    payResult,
    PointPayType,
    seed,
    SeedPayType,
    carrot,
    CarrotPayType,
  ]);

  const handleMore = (item: string) => {
    if (item === isHover) {
      setIsHover("");
      return;
    }
    setIsHover(item);
  };

  const DataFilter = [
    { name: "이번주", value: "this_week" },
    { name: "이번달", value: "this_month" },
    { name: "지난달", value: "last_month" },
    { name: "맞춤 설정", value: "date" },
  ];

  const formatLabelsDate = (
    dateTime: string,
    value: string,
    isDay?: boolean,
  ) => {
    if (!dateTime) return "";

    let date = dateTime.split("T")[0];
    if (isDay) date = dateTime;
    const [yyyy, mm, dd] = date.split("-");
    if (value === "report") {
      return `${mm}.${dd}`;
    }

    return `${yyyy}.${mm}.${dd}`;
  };

  return (
    <ReportWrapper onClick={() => setCalenderOpen(false)}>
      <ReportHeader>
        <div className="title">정산 리포트</div>
        <ReportFilterWrapper>
          <ReportFilterHeader>
            <div className="name">정산일</div>
            <div className="period">{reportDate} </div>
          </ReportFilterHeader>
          <div className="radio">
            {DataFilter.map((item) => (
              <RadioBox>
                {dateFilterType === item.value && (
                  <img src="/image-web/solution/checked.svg" alt="checked" />
                )}
                <input
                  type="radio"
                  checked={dateFilterType === item.value ? true : false}
                  onClick={(e: any) => {
                    if (item.value === "date") {
                      e.stopPropagation();
                      setCalenderOpen(!calenderOpen);
                    }
                  }}
                  onChange={() => {
                    setDateFilterType(item.value);
                    if (item.value === "date") {
                      setCalenderOpen(true);
                    } else {
                      setCalenderOpen(false);
                    }
                  }}
                />
                <span>{item.name}</span>
                {item.value === "date" && (
                  <ReportCalender
                    firstDate={firstDate}
                    setFirstDate={setFirstDate}
                    setLastDate={setLastDate}
                    calenderOpen={calenderOpen}
                    setCalenderOpen={setCalenderOpen}
                    getData={getData}
                    type={"report"}
                  />
                )}
              </RadioBox>
            ))}
          </div>
        </ReportFilterWrapper>
      </ReportHeader>
      <MainWrapper>
        <PageContentHeader>
          <div className="title">합계</div>
          <ReportContentTable>
            <div className="thead">
              {Financial.map((item, index) => (
                <ReportContentTableHeaderItem
                  key={index}
                  isLast={index === Financial.length - 1}
                >
                  {item.name}
                  {isHover === item.name && (
                    <ReportMoreInfo>
                      <MoreInfo
                        name={
                          index >= 4
                            ? `${item.name}(${item.unit}) 내역`
                            : item.name
                        }
                        dialogPayType={item.type ? item.type : []}
                      />
                    </ReportMoreInfo>
                  )}
                </ReportContentTableHeaderItem>
              ))}
            </div>
            <div className="tbody">
              {Financial.map((item, index) => (
                <ReportContentTableBodyItem
                  isMore={index !== 0 && index !== 3}
                  key={index}
                  onClick={() => {
                    if (index !== 0 && index !== 3) handleMore(item.name);
                  }}
                >
                  {item.value}
                </ReportContentTableBodyItem>
              ))}
            </div>
          </ReportContentTable>
        </PageContentHeader>
        <ReportGraph
          reportData={report}
          firstDate={firstDate}
          lastDate={lastDate}
          setFirstDate={setFirstDate}
          setLastDate={setLastDate}
          setDailyDate={setDailyDate}
          setIsReport={setIsReport}
          dateFilterType={dateFilterType}
        />
      </MainWrapper>
    </ReportWrapper>
  );
};

export default ReportPage;
